import { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { CalendarDays, MapPin, Minus, Plus, Users } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { pub, money, errMsg } from "@/lib/api";
import { HeaderLogo } from "@/components/Brand";
import { PoweredBy, Spinner, PracticeBanner } from "@/components/Layout";
import { TotalsBlock } from "@/components/CartSheet";

const calc = (types, qty, venue) => {
  const sub = types.reduce((a, t) => a + t.price_cents * (qty[t.id] || 0), 0);
  const taxable = types.filter((t) => !t.tax_exempt).reduce((a, t) => a + t.price_cents * (qty[t.id] || 0), 0);
  const gst = venue.charge_gst ? Math.round((taxable * venue.gst_rate) / 100) : 0;
  return { subtotal_cents: sub, convenience_fee_cents: 0, gst_cents: gst, gst_rate: venue.gst_rate, charge_gst: venue.charge_gst, total_cents: sub + gst };
};

const TicketRow = ({ t, q, setQ }) => (
  <li className="flex items-center justify-between py-3" data-testid={`ticket-type-${t.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
    <div><div className="font-heading font-extrabold">{t.name}</div><div className="text-sm">{t.price_cents ? money(t.price_cents) : "Free"}</div></div>
    <div className="flex items-center gap-3 rounded-full border-2 border-line px-2 py-1">
      <button aria-label="Fewer" onClick={() => setQ(Math.max(0, q - 1))} data-testid="ticket-qty-minus"><Minus size={16} /></button>
      <span className="w-5 text-center font-bold" data-testid="ticket-qty">{q}</span>
      <button aria-label="More" onClick={() => setQ(Math.min(10, q + 1))} data-testid="ticket-qty-plus"><Plus size={16} /></button>
    </div>
  </li>
);

export default function EventPage() {
  const { eventId } = useParams();
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [e, setE] = useState(null);
  const [qty, setQty] = useState({});
  const [name, setName] = useState(localStorage.getItem("lg_name") || "");
  const [email, setEmail] = useState(localStorage.getItem("lg_email") || "");
  const [busy, setBusy] = useState(false);

  useEffect(() => { pub.get(`/events/${eventId}`).then((r) => { setE(r.data); document.title = `${r.data.name} · LineGone`; }).catch((x) => toast.error(errMsg(x))); }, [eventId]);
  useEffect(() => { if (params.get("cancelled")) toast("Payment cancelled — no pass was issued."); }, [params]);
  if (!e) return <Spinner />;
  const t = calc(e.ticket_types, qty, e.venue);
  const count = Object.values(qty).reduce((a, b) => a + b, 0);

  const submit = async () => {
    if (!name.trim()) return toast.error("Please enter your name");
    if (!count) return toast.error("Pick at least one pass");
    setBusy(true);
    try {
      const r = await pub.post(`/events/${eventId}/register`, { name: name.trim(), email: email.trim() || null, origin_url: window.location.origin,
        tickets: Object.entries(qty).map(([ticket_type_id, q]) => ({ ticket_type_id, qty: q })) });
      if (r.data.checkout_url) window.location.href = r.data.checkout_url; else nav(`/p/${r.data.registration_id}`);
    } catch (x) { toast.error(errMsg(x)); setBusy(false); }
  };

  return (
    <div className="min-h-screen" data-testid="event-page">
      <header className="flex h-14 items-center border-b border-line px-4"><HeaderLogo className="h-6" /></header>
      {e.venue.practice && <PracticeBanner text="PRACTICE: no real payment. Pay with a Stripe test card." testid="event-practice-banner" />}
      <section className="bg-brand-pale px-4 py-8">
        <div className="mx-auto max-w-xl">
          <div className="eyebrow">{e.venue.name}{e.venue.is_demo ? " · DEMO" : ""}</div>
          <h1 className="mt-1 text-4xl font-black" data-testid="event-name">{e.name}</h1>
          <p className="mt-3 flex items-center gap-2 text-sm"><CalendarDays size={16} /> {e.date} · {e.time}</p>
          {e.location && <p className="mt-1 flex items-center gap-2 text-sm"><MapPin size={16} /> {e.location}</p>}
          <p className="mt-1 flex items-center gap-2 text-sm" data-testid="event-spots-left"><Users size={16} /> {e.spots_left} of {e.capacity} spots left</p>
          {e.description && <p className="mt-4 text-muted2">{e.description}</p>}
        </div>
      </section>
      <main className="mx-auto max-w-xl px-4 py-6">
        <ul className="divide-y divide-line">{e.ticket_types.map((tt) => <TicketRow key={tt.id} t={tt} q={qty[tt.id] || 0} setQ={(q) => setQty({ ...qty, [tt.id]: q })} />)}</ul>
        <div className="mt-4 grid gap-3">
          <div><Label htmlFor="reg-name">Name on pass</Label><Input id="reg-name" value={name} onChange={(x) => setName(x.target.value)} data-testid="register-name-input" /></div>
          <div><Label htmlFor="reg-email">Email (we'll send your QR pass)</Label><Input id="reg-email" type="email" value={email} onChange={(x) => setEmail(x.target.value)} data-testid="register-email-input" /></div>
        </div>
        {t.total_cents > 0 && <div className="mt-5 rounded-2xl bg-brand-pale p-4"><TotalsBlock t={t} testPrefix="register" /></div>}
        <button className="btn-primary mt-5 w-full !py-4 text-lg" disabled={busy || !count || e.spots_left < count} onClick={submit} data-testid="register-submit-btn">
          {busy ? "Working…" : t.total_cents > 0 ? `Pay ${money(t.total_cents)} & get pass` : "Get my free pass"}
        </button>
        <p className="mt-3 text-center text-xs text-muted2">Paid passes via Stripe · <Link to="/terms" className="link-lg">Terms</Link> · <Link to="/privacy" className="link-lg">Privacy</Link></p>
        <PoweredBy />
      </main>
    </div>
  );
}
